import { useState } from 'react'
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Shield, 
  Target, 
  Clock, 
  DollarSign,
  Building2,
  FileText,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react'
import { Business, TLBHScore, RiskAssessment, RiskFactor } from '@/types'

interface RiskAssessmentDashboardProps {
  business: Business
  score: TLBHScore
  riskAssessment: RiskAssessment
}

export function RiskAssessmentDashboard({ business, score, riskAssessment }: RiskAssessmentDashboardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'factors' | 'trends' | 'recommendations'>('overview')

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'high': return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900'
      case 'critical': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'low': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'high': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getLikelihoodColor = (likelihood: string) => {
    switch (likelihood) {
      case 'low': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'high': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'factors', label: 'Risk Factors', icon: AlertTriangle },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
    { id: 'recommendations', label: 'Recommendations', icon: Target }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Risk Assessment
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {business.name} • {business.industry}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {riskAssessment.riskScore}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Risk Score
              </div>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${getRiskLevelColor(riskAssessment.overallRisk)}`}>
              {riskAssessment.overallRisk.charAt(0).toUpperCase() + riskAssessment.overallRisk.slice(1)} Risk
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                TLBH Score
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {score.tlbhScore}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Band {score.band}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Confidence
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {score.confidence.toFixed(0)}%
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Data Quality
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Active Alerts
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {score.redFlags.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Red Flags
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Last Updated
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatDate(riskAssessment.assessmentDate).split(',')[0]}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Assessment Date
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Risk Score Breakdown */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Risk Score Breakdown
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    {Object.entries(score.dimensionScores).map(([key, dimension]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                          {key}
                        </span>
                        <div className="flex items-center space-x-3">
                          <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${dimension.score}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white w-8">
                            {dimension.score}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                      Assessment Summary
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Overall Risk:</span>
                        <span className={`font-medium ${getRiskLevelColor(riskAssessment.overallRisk).split(' ')[0]}`}>
                          {riskAssessment.overallRisk.charAt(0).toUpperCase() + riskAssessment.overallRisk.slice(1)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Risk Factors:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {riskAssessment.riskFactors.length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Valid Until:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(riskAssessment.validUntil)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Assessor:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {riskAssessment.assessor}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Red Flags */}
              {score.redFlags.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Active Red Flags
                  </h3>
                  <div className="space-y-3">
                    {score.redFlags.slice(0, 3).map((flag) => (
                      <div key={flag.id} className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-red-900 dark:text-red-100">
                              {flag.title}
                            </h4>
                            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                              {flag.description}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-red-600 dark:text-red-400">
                              <span>Severity: {flag.severity}</span>
                              <span>Impact: {flag.impact} points</span>
                              <span>Detected: {formatDate(flag.detectedAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Risk Factors Tab */}
          {activeTab === 'factors' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Identified Risk Factors
                </h3>
                <div className="space-y-4">
                  {riskAssessment.riskFactors.map((factor, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {factor.factor}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Category: {factor.category}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-center">
                            <div className={`w-3 h-3 rounded-full ${getImpactColor(factor.impact)} mb-1`} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Impact</span>
                          </div>
                          <div className="text-center">
                            <div className={`w-3 h-3 rounded-full ${getLikelihoodColor(factor.likelihood)} mb-1`} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Likelihood</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        {factor.description}
                      </p>
                      
                      {factor.mitigation && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                          <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                            Mitigation Strategy
                          </h5>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            {factor.mitigation}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Trends Tab */}
          {activeTab === 'trends' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Risk Trend Analysis
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Historical risk trend charts and analysis would be implemented here showing risk evolution over time.
                </p>
              </div>
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommendations' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Risk Mitigation Recommendations
                </h3>
                <div className="space-y-4">
                  {riskAssessment.recommendations.map((recommendation, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Target className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}